import {
  FaBookOpen,
  FaBoxesStacked,
  FaBullseye,
  FaFileInvoiceDollar,
  FaHandHoldingDollar,
  FaHandHoldingHeart,
  FaPiggyBank,
} from "react-icons/fa6";

import type { FeatureModule } from "./types";

export const keuanganModules: FeatureModule[] = [
  {
    slug: "tagihan",
    label: "Tagihan & Pembayaran",
    navDescription: "Syahriah, kitab, seragam, study tour",
    icon: FaFileInvoiceDollar,
    group: "keuangan",
    summary:
      "Buat dan tagih semua jenis pembayaran sekolah dari satu tempat, lalu terima pembayarannya secara online.",
    detail: {
      title: "Tagihan sekolah yang *tertagih sendiri*, bukan dikejar satu-satu",
      lead: "Semua jenis tagihan dalam satu daftar — syahriah, kitab, LKS, seragam, study tour, sampai infaq lembaga",
      intro:
        "Bendahara membuat tagihan sekali, lalu sistem yang menagih. Orang tua melihat rincian per jenis tagihan di aplikasi, membayar sebagian atau sekaligus, dan status di dashboard sekolah ikut berubah saat itu juga. Tidak ada lagi rekap manual di spreadsheet.",
      highlights: [
        "Buat massal atau impor tagihan dari file, per kelas atau per siswa",
        "Status akurat: belum terbayar, terbayar sebagian, dan lunas",
        "Orang tua bayar satuan atau sekaligus lewat aplikasi CARDS",
      ],
      benefits: [
        {
          title: "Sekali buat, langsung terbit ke semua siswa",
          body: "Terbitkan tagihan per rombel, angkatan, atau seluruh sekolah. Bisa juga impor dari file kalau data tagihannya sudah Anda susun sendiri.",
        },
        {
          title: "Cicilan tercatat rapi",
          body: "Pembayaran sebagian tidak merusak pembukuan. Tagihan bertanda “Terbayar Sebagian” dengan sisa yang selalu terhitung otomatis.",
        },
        {
          title: "Cari cepat waktu ditanya orang tua",
          body: "Telusuri berdasarkan nama, jenis tagihan, REF ID, atau catatan. Filter tanggal dan jenis untuk mempersempit daftar panjang.",
        },
        {
          title: "Laporan siap tanpa direkap",
          body: "Laporan dan rekap pembayaran terbentuk dari transaksi yang sudah masuk, termasuk pencatatan bukti pembayaran tunai di loket.",
        },
      ],
      screensTitle: "Dua sisi yang selalu sinkron",
      screensLead:
        "Bendahara bekerja di dashboard sekolah, orang tua melihat hasilnya di aplikasi — tanpa ada yang perlu saling mengabari.",
      screens: [
        {
          src: "/image/fitur/tagihan/desktop-data-tagihan.webp",
          alt: "Halaman Data Tagihan di dashboard Cards School",
          caption: "Data Tagihan",
          note: "Semua tagihan sekolah dengan status dan filternya",
          kind: "desktop",
        },
        {
          src: "/image/fitur/tagihan/mobile-tagihan.webp",
          alt: "Daftar tagihan siswa di aplikasi CARDS untuk orang tua",
          caption: "Tagihan di aplikasi orang tua",
          note: "Rincian per jenis, bayar satuan atau Bayar Semua",
          kind: "mobile",
        },
        {
          src: "/image/fitur/tagihan/desktop-laporan-pembayaran.webp",
          alt: "Laporan pembayaran dan bukti pembayaran tunai",
          caption: "Laporan Pembayaran",
          note: "Termasuk pencatatan bukti pembayaran tunai",
          kind: "desktop",
        },
      ],
      faq: [
        {
          q: "Bisakah jenis tagihan disesuaikan dengan istilah sekolah kami?",
          a: "Bisa. Jenis tagihan Anda tentukan sendiri — syahriah, SPP, uang makan, kitab, LKS, seragam, study tour, infaq lembaga, atau nama lain yang biasa dipakai di sekolah Anda.",
        },
        {
          q: "Bagaimana kalau orang tua membayar sebagian?",
          a: "Tagihan akan berstatus “Terbayar Sebagian” dan sisa tagihannya dihitung otomatis. Orang tua bisa melanjutkan pembayaran kapan saja sampai lunas.",
        },
        {
          q: "Kalau ada yang membayar tunai di loket sekolah?",
          a: "Petugas mencatat pembayaran tunai di dashboard dan bukti pembayarannya tersimpan pada tagihan tersebut, sehingga laporan tetap menyatu dengan pembayaran online.",
        },
      ],
      metaTitle: "Aplikasi Tagihan & Pembayaran Sekolah Online",
      metaDescription:
        "Kelola tagihan syahriah, kitab, seragam, hingga study tour dalam satu sistem. Orang tua bayar lewat aplikasi, status dan laporan sekolah terbarui otomatis.",
      keywords: [
        "aplikasi tagihan sekolah",
        "pembayaran spp online",
        "software tagihan sekolah",
        "aplikasi pembayaran sekolah",
        "sistem tagihan syahriah",
      ],
    },
  },

  {
    slug: "cicilan-tunggakan",
    label: "Cicilan & Tunggakan",
    navDescription: "Terbayar sebagian & laporan tunggakan",
    icon: FaHandHoldingDollar,
    group: "keuangan",
    summary:
      "Pantau siapa yang menunggak dan berapa sisanya, serta buka opsi cicilan lewat FlexyCazh.",
    detail: {
      title: "Tunggakan yang *terlihat sejak awal*, bukan saat sudah menumpuk",
      lead: "Pembayaran bertahap tercatat rapi, dan sisa tagihan tiap siswa selalu terhitung",
      intro:
        "Tidak semua orang tua bisa melunasi sekaligus, dan itu bukan alasan pembukuan jadi berantakan. Setiap pembayaran sebagian langsung mengurangi sisa tagihan, statusnya berubah sendiri, dan daftar tunggakan tersusun tanpa perlu ada yang menghitung ulang tiap bulan.",
      highlights: [
        "Sisa tagihan terhitung otomatis setiap kali ada pembayaran masuk",
        "Daftar tunggakan tersusun sendiri, siap dijadikan bahan tindak lanjut",
        "Opsi cicilan tanpa bunga lewat FlexyCazh untuk yang membutuhkan",
      ],
      benefits: [
        {
          title: "Bayar berapa pun, catatannya tetap benar",
          body: "Orang tua bisa membayar sebagian sesuai kemampuan. Sistem menandai tagihan sebagai terbayar sebagian dan menyimpan sisanya, bukan menolak pembayaran yang tidak genap.",
        },
        {
          title: "Tahu siapa yang perlu didekati",
          body: "Daftar tunggakan menunjukkan siswa, jenis tagihan, dan nominal yang tersisa — dasar yang jelas sebelum wali kelas menghubungi orang tua.",
        },
        {
          title: "Riwayat yang bisa ditunjukkan",
          body: "Tiap pembayaran punya waktu, nominal, dan statusnya sendiri. Kalau ada perbedaan pemahaman dengan orang tua, riwayatnya bisa langsung dibuka.",
        },
        {
          title: "Cicilan resmi lewat FlexyCazh",
          body: "Untuk beban yang besar seperti uang gedung, FlexyCazh memungkinkan pembayaran dicicil tanpa bunga sehingga sekolah tetap menerima penuh.",
        },
      ],
      screensTitle: "Yang dilihat orang tua saat mencicil",
      screensLead:
        "Proses bayar dan riwayatnya dibuat terbuka, supaya tidak ada pertanyaan soal uang yang sudah masuk.",
      screens: [
        {
          src: "/image/fitur/cicilan-tunggakan/mobile-pembayaran.webp",
          alt: "Proses pembayaran tagihan di aplikasi CARDS",
          caption: "Pembayaran",
          note: "Bayar sebagian atau lunas, dengan rincian yang jelas",
          kind: "mobile",
        },
        {
          src: "/image/fitur/cicilan-tunggakan/mobile-riwayat.webp",
          alt: "Detail riwayat transaksi pembayaran tagihan yang sukses",
          caption: "Riwayat Pembayaran",
          note: "Bukti tiap pembayaran, lengkap dengan statusnya",
          kind: "mobile",
        },
      ],
      faq: [
        {
          q: "Apakah sekolah bisa menentukan besaran cicilan minimum?",
          a: "Kebijakan cicilan mengikuti aturan sekolah. Anda menentukan sendiri jenis tagihan mana yang boleh dicicil dan bagaimana ketentuannya, lalu sistem yang mencatat sisanya.",
        },
        {
          q: "Apa bedanya cicilan biasa dengan FlexyCazh?",
          a: "Cicilan biasa adalah pembayaran bertahap langsung ke sekolah. FlexyCazh adalah skema cicilan tanpa bunga untuk orang tua, sementara sekolah tetap menerima pembayaran secara penuh.",
        },
        {
          q: "Bisakah laporan tunggakan diambil per kelas?",
          a: "Bisa. Daftar tunggakan dapat difilter dan dicari, sehingga wali kelas hanya melihat siswa di kelasnya sendiri.",
        },
      ],
      metaTitle: "Kelola Cicilan & Tunggakan SPP Sekolah",
      metaDescription:
        "Pembayaran bertahap tercatat otomatis dan sisa tagihan selalu terhitung. Daftar tunggakan tersusun sendiri, dengan opsi cicilan tanpa bunga lewat FlexyCazh.",
      keywords: [
        "aplikasi tunggakan spp",
        "cicilan spp sekolah",
        "laporan tunggakan sekolah",
        "pembayaran spp bertahap",
        "manajemen piutang sekolah",
      ],
    },
  },

  {
    slug: "tabungan",
    label: "Tabungan Sekolah",
    navDescription: "Produk & rekening tabungan siswa",
    icon: FaPiggyBank,
    group: "keuangan",
    summary:
      "Kelola produk tabungan dan rekening siswa dengan setor–tarik yang tercatat dan bisa dilihat orang tua.",
    detail: {
      title: "Tabungan siswa yang *dikelola seperti bank*, bukan buku tulis",
      lead: "Produk tabungan, rekening per siswa, dan transaksi setor–tarik yang setiap langkahnya terekam",
      intro:
        "Sekolah menentukan produk tabungannya sendiri, lalu membuka rekening untuk siswa, guru, maupun staf — bisa satu per satu atau massal sekaligus. Setiap setoran dan penarikan tercatat dengan jenis transaksi, waktu, dan konfirmasi PIN, sehingga saldo tiap rekening selalu bisa dipertanggungjawabkan.",
      highlights: [
        "Beberapa produk tabungan sekaligus, sesuai program sekolah",
        "Buka rekening massal untuk siswa, guru, atau staf",
        "Transaksi tunai maupun wallet dengan konfirmasi PIN",
      ],
      benefits: [
        {
          title: "Produk tabungan mengikuti program Anda",
          body: "Buat lebih dari satu produk tabungan dan tetapkan ketentuannya masing-masing, lalu daftar rekening bisa difilter per produk maupun per instansi.",
        },
        {
          title: "Buka rekening tanpa satu-satu",
          body: "Pembuatan rekening massal tersedia untuk siswa, guru, dan staf — jauh lebih cepat daripada mendaftarkan satu per satu di awal tahun ajaran.",
        },
        {
          title: "Setiap transaksi ada konfirmasinya",
          body: "Setoran dan penarikan, baik tunai maupun dari wallet, melewati konfirmasi berPIN. Salah PIN ditolak, jadi tidak ada transaksi yang lolos tanpa persetujuan.",
        },
        {
          title: "Rekening bisa dikendalikan",
          body: "Rekening dapat diblokir, dinonaktifkan, diaktifkan kembali, sampai dicetak kartunya — semua dari halaman detail rekening yang sama.",
        },
      ],
      screensTitle: "Dari produk sampai mutasi rekening",
      screensLead:
        "Petugas tabungan bekerja dari dua halaman: daftar rekening untuk gambaran menyeluruh, detail rekening untuk transaksi harian.",
      screens: [
        {
          src: "/image/fitur/tabungan/desktop-produk.webp",
          alt: "Daftar rekening tabungan per produk di dashboard Cards School",
          caption: "Rekening per Produk",
          note: "Filter per produk, instansi, dan status rekening",
          kind: "desktop",
        },
        {
          src: "/image/fitur/tabungan/desktop-rekening.webp",
          alt: "Detail rekening tabungan siswa dengan riwayat transaksi",
          caption: "Detail Rekening",
          note: "Mutasi, buat transaksi, blokir, dan cetak kartu",
          kind: "desktop",
        },
      ],
      faq: [
        {
          q: "Apakah tabungan ini terpisah dari saldo kartu siswa?",
          a: "Ya, keduanya terpisah. Saldo kartu dipakai untuk transaksi harian seperti kantin, sedangkan tabungan adalah simpanan dengan produk dan ketentuannya sendiri.",
        },
        {
          q: "Bisakah orang tua melihat saldo tabungan anaknya?",
          a: "Bisa. Tabungan sekolah muncul di aplikasi orang tua, sehingga saldo dan mutasinya terbuka tanpa perlu bertanya ke petugas.",
        },
        {
          q: "Bagaimana kalau siswa pindah atau lulus?",
          a: "Rekening dapat dinonaktifkan atau diblokir lebih dulu sebelum saldo diselesaikan, dan statusnya tercatat sehingga tidak tercampur dengan rekening aktif.",
        },
      ],
      metaTitle: "Aplikasi Tabungan Siswa & Rekening Sekolah",
      metaDescription:
        "Kelola produk tabungan dan rekening siswa, guru, dan staf. Setoran dan penarikan berkonfirmasi PIN, mutasi terekam, saldo terbuka untuk orang tua.",
      keywords: [
        "aplikasi tabungan siswa",
        "tabungan sekolah digital",
        "rekening tabungan siswa",
        "software tabungan sekolah",
        "sistem simpanan siswa",
      ],
    },
  },

  {
    slug: "tabungan-cita",
    label: "Tabungan Cita",
    navDescription: "Tabungan bertujuan & berjangka",
    icon: FaBullseye,
    group: "keuangan",
    summary:
      "Tabungan dengan tujuan dan jangka waktu — umroh, wisuda, atau study tour — yang progresnya terlihat.",
    detail: {
      title: "Menabung untuk *tujuan yang jelas*, bukan sekadar menyisihkan",
      lead: "Tabungan berjangka dengan target dan durasi, sehingga orang tua tahu sedang menuju apa",
      intro:
        "Beban besar seperti umroh, wisuda, atau study tour terasa ringan kalau dicicil jauh hari. Tabungan Cita memberi setiap tujuan nominal target dan durasinya sendiri, lalu menampilkan progresnya di aplikasi orang tua — jadi menabung punya arah, bukan sekadar menaruh uang.",
      highlights: [
        "Satu tujuan, satu target nominal, dan durasi tabungannya sendiri",
        "Progres terlihat langsung di beranda aplikasi orang tua",
        "Bisa berjalan berdampingan dengan tabungan sekolah biasa",
      ],
      benefits: [
        {
          title: "Tujuan yang punya nama",
          body: "Bukan sekadar angka saldo. Tiap tabungan diberi nama tujuan beserta nominal targetnya, sehingga orang tua paham betul untuk apa uang itu dikumpulkan.",
        },
        {
          title: "Durasi yang membuat rencananya nyata",
          body: "Setiap tujuan punya jangka waktu, misalnya dua belas bulan. Dari situ orang tua bisa memperkirakan berapa yang perlu disisihkan tiap bulan.",
        },
        {
          title: "Beban besar jadi terpecah",
          body: "Biaya yang berat kalau dibayar sekaligus menjadi jauh lebih ringan ketika dikumpulkan sejak awal tahun, dan sekolah tidak perlu menagih mendadak.",
        },
        {
          title: "Terlihat setiap kali aplikasi dibuka",
          body: "Progres tabungan tampil di beranda, bukan terkubur di menu dalam. Pengingat yang halus supaya rencananya tidak terlupakan.",
        },
      ],
      screensTitle: "Tujuan menabung di beranda orang tua",
      screensLead:
        "Ditempatkan sejajar dengan saldo dan tagihan, supaya progresnya terbaca sekilas.",
      screens: [
        {
          src: "/image/fitur/tabungan-cita/crop-tabungan-cita.webp",
          alt: "Kartu Tabungan Cita dengan target dan durasi di aplikasi CARDS",
          caption: "Tabungan Cita",
          note: "Target nominal dan durasi tiap tujuan",
          kind: "crop",
        },
      ],
      faq: [
        {
          q: "Tujuan tabungannya bisa ditentukan sekolah?",
          a: "Bisa. Sekolah menentukan tujuan apa saja yang dibuka — umroh, wisuda, study tour, atau program lain — beserta nominal target dan durasinya.",
        },
        {
          q: "Apakah dana Tabungan Cita bisa diambil sewaktu-waktu?",
          a: "Ketentuan pengambilan mengikuti kebijakan sekolah atas produk tabungan tersebut, karena Tabungan Cita dirancang sebagai simpanan berjangka menuju satu tujuan.",
        },
        {
          q: "Bisakah satu siswa punya lebih dari satu tujuan?",
          a: "Bisa. Setiap tujuan berdiri sendiri dengan target dan durasinya masing-masing, dan semuanya tampil di aplikasi orang tua.",
        },
      ],
      metaTitle: "Tabungan Cita — Tabungan Berjangka Siswa",
      metaDescription:
        "Tabungan sekolah dengan tujuan dan jangka waktu, seperti umroh atau wisuda. Target dan progresnya terlihat langsung di aplikasi orang tua.",
      keywords: [
        "tabungan berjangka siswa",
        "tabungan tujuan sekolah",
        "tabungan umroh siswa",
        "tabungan wisuda sekolah",
        "program menabung siswa",
      ],
    },
  },

  {
    slug: "pembukuan",
    label: "Pembukuan & Laporan",
    navDescription: "Jurnal, laba/rugi, posisi keuangan",
    icon: FaBookOpen,
    group: "keuangan",
    summary:
      "Jurnal, riwayat jurnal, laporan posisi keuangan, laba/rugi, dan bagi hasil dalam satu pembukuan.",
    detail: {
      title: "Pembukuan yang *tumbuh dari transaksi*, bukan diketik ulang",
      lead: "Jurnal, laporan posisi keuangan, laba/rugi, dan bagi hasil dari data yang sudah ada di sistem",
      intro:
        "Tagihan, tabungan, dan transaksi kartu sudah tercatat di CARDS. Pembukuan mengambil dari sana, bukan meminta bendahara memasukkannya sekali lagi ke aplikasi terpisah. Pemasukan dan pengeluaran di luar transaksi otomatis dicatat lewat jurnal manual, riwayat perubahannya tersimpan, dan laporan bisa ditarik kapan pun dibutuhkan yayasan.",
      highlights: [
        "Jurnal otomatis dan jurnal manual, seluruhnya terekam dengan riwayat yang dapat diaudit",
        "Empat laporan standar: COA, Neraca, Laba/Rugi, dan Arus Kas — sesuai ISAK 35",
        "Perhitungan bagi hasil dan migrasi akun saat pindah sistem",
      ],
      benefits: [
        {
          title: "Satu sumber data, bukan dua",
          body: "Transaksi yang sudah masuk lewat tagihan, tabungan, dan kartu menjadi bahan pembukuan. Pemasukan atau pengeluaran di luar itu dicatat lewat jurnal manual — tetap di pembukuan yang sama, tanpa entri ganda yang menimbulkan selisih.",
        },
        {
          title: "Laporan yang diminta yayasan",
          body: "COA, Neraca, Laba/Rugi, dan Arus Kas sesuai ISAK 35 tersedia langsung, jadi permintaan mendadak menjelang rapat tidak lagi berarti lembur menyusun angka.",
        },
        {
          title: "Perubahan ada jejaknya",
          body: "Seluruh transaksi terekam, dan riwayat jurnal menyimpan apa yang berubah — koreksi tetap bisa ditelusuri dan pembukuan tetap dapat diaudit.",
        },
        {
          title: "Pindah sistem tanpa mulai dari nol",
          body: "Migrasi akun memungkinkan struktur akun yang sudah Anda pakai selama ini dibawa masuk, bukan dibangun ulang dari kosong.",
        },
      ],
      screensTitle: "Dari daftar akun sampai arus kas",
      screensLead:
        "Empat laporan standar — COA, Neraca, Laba/Rugi, dan Arus Kas — ditarik langsung dari pembukuan, mengikuti template Cazh sesuai ISAK 35.",
      screens: [
        {
          src: "/image/fitur/pembukuan/desktop-laporan-umum.webp",
          alt: "Halaman Laporan Umum dengan pilihan laporan COA, Neraca, Laba Rugi, dan Arus Kas",
          caption: "Laporan Umum",
          note: "Empat jenis laporan siap pakai: COA, Neraca, Laba Rugi, dan Arus Kas",
          kind: "desktop",
        },
        {
          src: "/image/fitur/pembukuan/desktop-coa.webp",
          alt: "Laporan COA berisi daftar akun per sub kelompok dengan saldo awal, mutasi, dan saldo akhir",
          caption: "COA",
          note: "Daftar akun per sub kelompok dengan saldo awal, debit, kredit, dan saldo akhir",
          kind: "desktop",
        },
        {
          src: "/image/fitur/pembukuan/desktop-neraca.webp",
          alt: "Laporan Neraca — Laporan Posisi Keuangan ISAK 35 dengan rincian aset lancar dan tidak lancar",
          caption: "Neraca",
          note: "Laporan Posisi Keuangan ISAK 35 — pilih kantor dan periodenya, laporan tersusun",
          kind: "desktop",
        },
        {
          src: "/image/fitur/pembukuan/desktop-arus-kas.webp",
          alt: "Laporan Arus Kas metode tidak langsung ISAK 35 dengan rincian aktivitas operasi dan investasi",
          caption: "Arus Kas",
          note: "Laporan Arus Kas (metode tidak langsung) ISAK 35, dari aktivitas operasi sampai investasi",
          kind: "desktop",
        },
        {
          src: "/image/fitur/pembukuan/crop-jurnal-manual.webp",
          alt: "Form Jurnal Manual dengan akun debit, akun kredit, jumlah, dan unggahan lampiran bukti",
          caption: "Jurnal Manual",
          note: "Pemasukan atau pengeluaran di luar transaksi otomatis dicatat manual, lengkap dengan lampiran bukti",
          kind: "crop",
        },
        {
          src: "/image/fitur/pembukuan/crop-neraca-seimbang.webp",
          alt: "Bagian bawah neraca yang menampilkan selisih nol berlabel Seimbang",
          caption: "Neraca mengecek dirinya sendiri",
          note: "Selisih total aset terhadap liabilitas & aset neto harus 0 — berlabel Seimbang",
          kind: "crop",
        },
      ],
      faq: [
        {
          q: "Bagaimana mencatat pemasukan atau pengeluaran yang tidak lewat CARDS?",
          a: "Lewat Jurnal Manual: pilih akun debit dan kredit, isi jumlah dan catatan, lalu lampirkan bukti transaksinya. Entri manual ini terekam di jurnal yang sama beserta riwayatnya, sehingga tetap dapat diaudit.",
        },
        {
          q: "Apakah pembukuan ini menggantikan aplikasi akuntansi kami?",
          a: "Pembukuan CARDS mencakup jurnal, laporan posisi keuangan, laba/rugi, dan bagi hasil atas transaksi yang berjalan di CARDS. Untuk kebutuhan akuntansi yayasan yang lebih luas, tim kami dapat membahas cakupannya bersama Anda.",
        },
        {
          q: "Bisakah struktur akun kami yang lama dipakai?",
          a: "Bisa. Tersedia migrasi akun sehingga daftar akun yang sudah Anda gunakan dapat dibawa ke sistem, tidak perlu disusun ulang.",
        },
        {
          q: "Siapa saja yang bisa membuka laporan keuangan?",
          a: "Akses mengikuti pengaturan pengguna di dashboard, sehingga laporan keuangan hanya terbuka bagi peran yang Anda beri izin.",
        },
      ],
      metaTitle: "Pembukuan & Laporan Keuangan Sekolah",
      metaDescription:
        "Jurnal, laporan posisi keuangan, laba/rugi, dan bagi hasil yang terbentuk dari transaksi tagihan, tabungan, dan kartu — tanpa entri ulang.",
      keywords: [
        "pembukuan sekolah",
        "laporan keuangan sekolah",
        "aplikasi akuntansi sekolah",
        "jurnal keuangan sekolah",
        "laporan laba rugi sekolah",
      ],
    },
  },

  {
    slug: "donasi",
    label: "Donasi",
    navDescription: "Penggalangan dana transparan",
    icon: FaHandHoldingHeart,
    group: "keuangan",
    summary:
      "Buka penggalangan dana sekolah dengan pencatatan dan laporan yang bisa dipertanggungjawabkan.",
    detail: {
      title: "Penggalangan dana yang *bisa dipertanggungjawabkan*",
      lead: "Donasi masuk lewat kanal yang sama dengan pembayaran lain, dan tercatat sejak rupiah pertama",
      intro:
        "Pembangunan masjid, bantuan siswa kurang mampu, atau musibah yang mendadak — penggalangan dana sering berjalan lewat rekening pribadi dan catatan manual. Modul donasi memindahkannya ke kanal resmi sekolah, sehingga setiap penerimaan tercatat dan laporannya bisa ditunjukkan ke wali murid.",
      highlights: [
        "Donasi masuk lewat kanal pembayaran resmi sekolah",
        "Tercatat otomatis, bukan direkap manual dari mutasi rekening",
        "Menyatu dengan pembukuan sekolah",
      ],
      benefits: [
        {
          title: "Satu pintu dengan pembayaran lain",
          body: "Orang tua berdonasi dari aplikasi yang sama yang mereka pakai untuk membayar tagihan, tanpa perlu transfer manual ke rekening terpisah.",
        },
        {
          title: "Catatan yang lengkap sejak awal",
          body: "Setiap donasi tersimpan beserta waktu dan nominalnya, jadi tidak ada penerimaan yang luput karena terlewat dicatat.",
        },
        {
          title: "Laporan yang menjaga kepercayaan",
          body: "Karena tercatat sistematis, laporan penggunaan dana bisa disusun dari data yang sama — bukan dari ingatan atau kumpulan bukti transfer.",
        },
        {
          title: "Menyatu dengan pembukuan",
          body: "Penerimaan donasi ikut masuk ke pembukuan sekolah, sehingga posisi keuangan tetap utuh tanpa pencatatan terpisah.",
        },
      ],
      screensTitle: "Alur donasi dari niat sampai laporan",
      screensLead:
        "Empat langkah yang membuat penggalangan dana berhenti bergantung pada catatan pribadi.",
      steps: [
        {
          title: "Sekolah membuka penggalangan",
          body: "Tentukan program donasinya — pembangunan, beasiswa, atau bantuan musibah — beserta keterangan yang perlu diketahui wali murid.",
        },
        {
          title: "Orang tua berdonasi",
          body: "Donasi dilakukan dari aplikasi CARDS, kanal yang sama dengan pembayaran tagihan, tanpa transfer manual.",
        },
        {
          title: "Penerimaan tercatat",
          body: "Setiap donasi tersimpan dengan waktu dan nominalnya, sehingga total terkumpul selalu terbarui.",
        },
        {
          title: "Laporan disusun",
          body: "Dari catatan yang sama, sekolah menyusun laporan penerimaan dan penggunaan dana untuk disampaikan kembali ke wali murid.",
        },
      ],
      faq: [
        {
          q: "Apakah donatur bisa berdonasi tanpa mencantumkan nama?",
          a: "Ketentuan pencantuman nama mengikuti kebijakan sekolah atas program donasi yang dibuka. Yang pasti, setiap penerimaan tetap tercatat di sistem.",
        },
        {
          q: "Bisakah dibuka lebih dari satu program donasi?",
          a: "Bisa. Program donasi dapat berjalan bersamaan sesuai kebutuhan sekolah, dan penerimaannya tercatat pada masing-masing program.",
        },
        {
          q: "Apakah donasi otomatis masuk laporan keuangan?",
          a: "Ya. Penerimaan donasi menjadi bagian dari pembukuan sekolah, sehingga tidak perlu dicatat ulang secara terpisah.",
        },
      ],
      metaTitle: "Donasi Sekolah Online yang Transparan",
      metaDescription:
        "Buka penggalangan dana sekolah lewat kanal pembayaran resmi. Setiap donasi tercatat otomatis dan menyatu dengan pembukuan, siap dilaporkan ke wali murid.",
      keywords: [
        "donasi sekolah online",
        "penggalangan dana sekolah",
        "aplikasi donasi sekolah",
        "infaq sekolah digital",
        "laporan donasi sekolah",
      ],
    },
  },
  {
    slug: "inventaris",
    label: "Inventaris",
    navDescription: "Aset & barang milik lembaga",
    icon: FaBoxesStacked,
    group: "keuangan",
    summary:
      "Catat aset dan barang milik sekolah dalam satu daftar yang tertata, bukan di buku tulis yang tercecer.",
    detail: {
      title: "Aset sekolah yang *tercatat*, bukan sekadar diingat",
      lead: "Inventaris barang milik lembaga dalam satu daftar dengan pengaturannya sendiri",
      intro:
        "Meja, komputer, alat peraga, sampai perangkat presensi — barang milik sekolah terus bertambah, dan catatannya sering tertinggal di buku tulis petugas. Modul inventaris memindahkannya ke sistem yang sama dengan data lembaga, sehingga daftar aset selalu bisa dibuka dan dipertanggungjawabkan.",
      highlights: [
        "Semua aset dan barang lembaga tercatat dalam satu daftar",
        "Pengaturan kategori mengikuti kebutuhan lembaga",
        "Berada di sistem yang sama dengan data dan keuangan sekolah",
      ],
      benefits: [
        {
          title: "Tidak lagi bergantung pada satu petugas",
          body: "Catatan aset tersimpan di sistem, bukan di buku pribadi. Pergantian petugas sarana prasarana tidak membuat riwayat barang ikut hilang.",
        },
        {
          title: "Serah terima yang jelas",
          body: "Daftar yang tertata membuat serah terima antar pengurus atau audit yayasan berangkat dari data, bukan dari menghitung ulang satu gudang.",
        },
        {
          title: "Kategori yang Anda tentukan",
          body: "Pengaturan inventaris memungkinkan pengelompokan mengikuti cara lembaga Anda memandang asetnya — per ruang, per jenis, atau per penanggung jawab.",
        },
        {
          title: "Satu sistem dengan keuangan",
          body: "Karena berada di CARDS, catatan aset berdampingan dengan pembukuan — memudahkan yayasan melihat kekayaan lembaga secara utuh.",
        },
      ],
      screensTitle: "Alur pencatatan inventaris",
      screensLead:
        "Dimulai dari pengaturan kategori, lalu pencatatan berjalan mengikuti barang yang masuk.",
      steps: [
        {
          title: "Atur kategorinya",
          body: "Tentukan pengelompokan aset lewat pengaturan inventaris, sesuai cara lembaga Anda menata barang.",
        },
        {
          title: "Catat barangnya",
          body: "Setiap aset dan barang milik lembaga dimasukkan ke daftar inventaris beserta keterangannya.",
        },
        {
          title: "Kelola dan mutakhirkan",
          body: "Perubahan kondisi atau keberadaan barang dimutakhirkan pada daftar yang sama, sehingga catatannya tetap hidup.",
        },
        {
          title: "Buka saat dibutuhkan",
          body: "Saat serah terima, audit, atau perencanaan pengadaan, daftar aset siap dibuka tanpa penghitungan ulang.",
        },
      ],
      faq: [
        {
          q: "Barang apa saja yang bisa dicatat?",
          a: "Semua aset dan barang milik lembaga — dari perabot ruang kelas sampai perangkat elektronik. Pengelompokannya Anda tentukan lewat pengaturan inventaris.",
        },
        {
          q: "Siapa yang boleh mengelola inventaris?",
          a: "Akses mengikuti pengaturan pengguna di dashboard, sehingga hanya peran yang Anda beri izin yang dapat menambah atau mengubah catatan aset.",
        },
        {
          q: "Apakah inventaris terhubung dengan pembukuan?",
          a: "Keduanya berada dalam sistem yang sama, sehingga catatan aset dapat dibaca berdampingan dengan laporan keuangan lembaga.",
        },
      ],
      metaTitle: "Aplikasi Inventaris Aset Sekolah & Pesantren",
      metaDescription:
        "Catat aset dan barang milik sekolah atau pesantren dalam satu daftar yang tertata — dengan kategori yang bisa diatur dan berada satu sistem dengan keuangan.",
      keywords: [
        "aplikasi inventaris sekolah",
        "pencatatan aset sekolah",
        "inventaris barang sekolah",
        "manajemen aset pesantren",
        "sarana prasarana sekolah digital",
      ],
    },
  },
];
